import { Text } from "@mantine/core";
import { FC } from "react";
import { AcknowledgementLayout } from "@funded-here-interface/common";
import { useStyle } from "./ThanksPage.styles";
import { ThanksPageType } from "../../constants/enums";

/**
 * Props for ThanksPage component
 */
interface ThanksPageProps {
  /** Type of thank you page to display (validated by ThanksPageWrapper) */
  type: ThanksPageType;
}

/**
 * Content structure for each thank you page variant
 */
interface ThanksPageContent {
  title: string;
  messages: string[];
}

/**
 * Content mapping for each ThanksPageType variant.
 * Centralized configuration for easy content updates.
 */
const THANKS_PAGE_CONTENT: Record<ThanksPageType, ThanksPageContent> = {
  [ThanksPageType.EKYC]: {
    title: "e-KYC Completed",
    messages: [
      "Your e-KYC process is complete.",
      "We will now review your submitted information and notify you once the review is complete.",
    ],
  },
  [ThanksPageType.ONBOARDING]: {
    title: "Onboarding Form Submitted",
    messages: [
      "Thank you for submitting your onboarding form.",
      "All directors of your company will receive an email with instructions to complete the e-KYC process.",
    ],
  },
  [ThanksPageType.ONBOARDING_SUPPLIER]: {
    title: "Onboarding Form Submitted",
    messages: [
      "Thank you for submitting your onboarding form.",
      "We will review your submitted information and notify you once the review is complete.",
    ],
  },
  [ThanksPageType.RESUBMITTED]: {
    title: "Onboarding Form Resubmitted",
    messages: [
      "Thank you for resubmitting your onboarding form.",
      "We will review your updated information and notify you once the review is complete.",
    ],
  },
  [ThanksPageType.RESUBMITTED_IND_INVESTOR]: {
    title: "Resubmission Successful",
    messages: [
      "Thank you for resubmitting your onboarding form and executing the latest agreement.",
      "We will review your updated information and notify you once the review is complete.",
    ],
  },
  [ThanksPageType.AGREEMENT]: {
    title: "Agreement Signing Successful",
    messages: [
      "Thank you for executing the agreement. You have now completed all the required onboarding steps.",
      "We will now review your submitted information and notify you once the review is complete.",
    ],
  },
  [ThanksPageType.COMPLETED]: {
    title: "Onboarding Completed",
    messages: [
      "Your onboarding process has been successfully completed. You may now reach out to your dedicated Client Success Consultant to submit your collaboration proposal.",
    ],
  },
  [ThanksPageType.BOARDING_RESOLUTION]: {
    title: "Board Resolution Signing Completed",
    messages: [
      "The Board Resolution has been successfully signed.",
      "Once all directors of your company have signed the Board Resolution, your company's authorized representative will receive an email with instructions to complete the e-KYC process and sign the required agreement(s).",
    ],
  },
  [ThanksPageType.PENDING_AGREEMENT]: {
    title: "Pending Agreement Execution",
    messages: [
      "We are currently waiting the appointed authorized representative of your company to complete the e-KYC process and execute the necessary agreements to complete the onboarding process.",
    ],
  },
  [ThanksPageType.DEFAULT]: {
    title: "Thank You!",
    messages: [
      "We appreciate the time & effort you have taken to submit the required information to us.",
      "Our team is reviewing your application. We will get back to you as soon as possible via Email, so you can continue your journey with us.",
      "Thank you for your patience.",
    ],
  },
};

/**
 * Thank you page component that displays different messages based on the completion type.
 * Should be used via ThanksPageWrapper which handles type validation and routing.
 *
 * @example
 * // Via ThanksPageWrapper (recommended)
 * <Route path="/thanks" element={<ThanksPageWrapper />} />
 *
 * @example
 * // Direct usage (type must be validated)
 * <ThanksPage type={ThanksPageType.EKYC} />
 */
const ThanksPage: FC<ThanksPageProps> = ({ type }) => {
  const { classes } = useStyle();

  // Direct lookup - ThanksPageWrapper guarantees valid type
  const content = THANKS_PAGE_CONTENT[type];

  return (
    <AcknowledgementLayout
      showLogo={false}
      content={
        <div className={classes.container}>
          <Text className={classes.headerText}>{content.title}</Text>
          {content.messages.map((message, index) => (
            <Text
              key={index}
              className={classes.text}
              style={
                type === ThanksPageType.BOARDING_RESOLUTION && index === 0
                  ? { marginTop: "25px" }
                  : undefined
              }
            >
              {message}
            </Text>
          ))}
        </div>
      }
    />
  );
};

export default ThanksPage;
